import * as Api from '@ViewModels';
import { AIConversationType, BrandingSettings, BrandImage } from '@ViewModels';
import { css } from 'aphrodite';
import * as React from 'react';
import ReactModal from 'react-modal';
import { toast } from 'sonner';
import { FontPicker } from '../../../components-v2/FontPicker';
import { useErrorMessages, useUserSession } from '../../../../models/hooks/appStateHooks';
import {
	invalidateBrandingSettingsQueries,
	useBrandingSettingsMutation,
	useBrandingSettingsQuery,
} from '../../../../queries/AccountProfile';
import { invalidateAllAIConversationFilterQueries } from '../../../../queries/AIConversation/useAIConversationFilterQuery';
import { AIAssistantDialog } from '../../../components-v2/ai/AIAssistantDialog';
import { HexColorRenderer } from '../../../components-v2/ai/AIConversationMessage/renderers';
import { useImageAttachmentMutation } from '../../../../queries/Attachments/useImageAttachmentMutation';
import { Button } from '../../../components-v2/Button';
import { ColorPicker } from '../../../components-v2/ColorPicker';
import { baseStyleSheet } from '../../../styles/styles';
import { DeprecatedCloseButton } from '../../DeprecatedCloseButton';
import { LoadingSpinner } from '../../LoadingSpinner';
import { EditItemPenIcon } from '../../svgs/icons/EditItemPenIcon';
import { TrashIcon } from '../../svgs/icons/TrashIcon';
import { imagePreviewModalStyles, styleSheet } from '../styles';

const DEFAULT_BRANDING: BrandingSettings = {};
const MAX_IMAGE_SIZE_BYTES = 5 * 1024 * 1024; // 5MB

interface IBrandSettingsSectionProps {
	/** Optional impersonation context. If not provided, uses the current user session's context. */
	impersonationContext?: Api.IImpersonationContext;
}

const BrandSettingsSectionComponent: React.FC<IBrandSettingsSectionProps> = ({
	impersonationContext: propImpersonationContext,
}) => {
	const userSession = useUserSession();
	const errorMessages = useErrorMessages();
	const impersonationContext = propImpersonationContext ?? userSession.impersonationContext;

	// Branding state
	const brandingQuery = useBrandingSettingsQuery({ impersonationContext });
	const brandingMutation = useBrandingSettingsMutation({
		onError: error => {
			errorMessages.pushApiError(error);
		},
		onSuccess: () => {
			setBrandingDirty(false);
			toast.success('Branding settings saved successfully');
		},
	});
	const [localBranding, setLocalBranding] = React.useState<BrandingSettings>(DEFAULT_BRANDING);
	const [brandingDirty, setBrandingDirty] = React.useState(false);
	const [uploadingImage, setUploadingImage] = React.useState(false);
	const [editingImageIndex, setEditingImageIndex] = React.useState<number | null>(null);
	const [previewImage, setPreviewImage] = React.useState<BrandImage | null>(null);
	const [brandingAssistantOpen, setBrandingAssistantOpen] = React.useState(false);
	const [waitingForAIRefresh, setWaitingForAIRefresh] = React.useState(false);
	const fileInputRef = React.useRef<HTMLInputElement>(null);

	// Image upload mutation
	const imageUploadMutation = useImageAttachmentMutation({
		onError: error => {
			errorMessages.pushApiError(error);
			setUploadingImage(false);
		},
	});

	// Sync branding query data to local state
	React.useEffect(() => {
		if (brandingQuery.data) {
			// Only sync server data to local state when:
			// 1. The user has no unsaved changes, OR
			// 2. We're explicitly refreshing after the AI dialog updated server data
			if (!brandingDirty || waitingForAIRefresh) {
				setLocalBranding(brandingQuery.data);
			}
			if (waitingForAIRefresh && !brandingQuery.isFetching) {
				setWaitingForAIRefresh(false);
				setBrandingDirty(false); // AI data is now authoritative; clear dirty flag
			}
		}
	}, [brandingQuery.data, brandingQuery.isFetching, waitingForAIRefresh, brandingDirty]);

	// Branding handlers
	const updateBrandingField = <K extends keyof BrandingSettings>(field: K, value: BrandingSettings[K]) => {
		setLocalBranding(prev => ({ ...prev, [field]: value }));
		setBrandingDirty(true);
	};

	const handleSaveBranding = () => {
		brandingMutation.mutate({
			brandingSettings: localBranding,
			impersonationContext,
		});
	};

	const handleCancelBranding = () => {
		if (brandingQuery.data) {
			setLocalBranding(brandingQuery.data);
		} else {
			setLocalBranding(DEFAULT_BRANDING);
		}
		setBrandingDirty(false);
	};

	const handleClearAllBranding = () => {
		setLocalBranding(DEFAULT_BRANDING);
		setEditingImageIndex(null);
		setBrandingDirty(true);
	};

	const handleRemoveImage = (index: number) => {
		const newImages = [...(localBranding.images || [])];
		newImages.splice(index, 1);
		updateBrandingField('images', newImages.length > 0 ? newImages : undefined);
	};

	const handleAddImageClick = () => {
		fileInputRef.current?.click();
	};

	const handleFileSelected = async (e: React.ChangeEvent<HTMLInputElement>) => {
		const file = e.target.files?.[0];
		if (!file) return;

		// Reset the input so the same file can be selected again
		e.target.value = '';

		// Validate file type
		if (!file.type.startsWith('image/')) {
			toast.error('Please select an image file');
			return;
		}

		// Validate file size (5MB limit)
		if (file.size > MAX_IMAGE_SIZE_BYTES) {
			toast.error('Image must be less than 5MB');
			return;
		}

		setUploadingImage(true);

		try {
			const attachments = await imageUploadMutation.mutateAsync({
				files: [file],
				impersonationContext,
			});

			if (attachments && attachments.length > 0) {
				const attachment = attachments[0];
				const newImage: BrandImage = {
					name: file.name.replace(/\.[^/.]+$/, ''), // Remove file extension for display name
					fileAttachmentId: attachment.id,
					description: '',
					fileAttachment: attachment,
				};
				const newImages = [...(localBranding.images || []), newImage];
				updateBrandingField('images', newImages);
				// Automatically enter edit mode for the new image so user can add description
				setEditingImageIndex(newImages.length - 1);
				toast.success('Image uploaded! Please add a description.');
			}
		} catch {
			// Error already handled in mutation onError
		} finally {
			setUploadingImage(false);
		}
	};

	const handleUpdateImageField = (
		index: number,
		field: keyof Pick<BrandImage, 'name' | 'description'>,
		value: string
	) => {
		const newImages = [...(localBranding.images || [])];
		newImages[index] = { ...newImages[index], [field]: value };
		updateBrandingField('images', newImages);
	};

	// Additional colors handlers
	const handleAddCustomColor = () => {
		const currentColors = localBranding.additionalColors || {};
		const newKey = `Color ${Object.keys(currentColors).length + 1}`;
		updateBrandingField('additionalColors', { ...currentColors, [newKey]: '#000000' });
	};

	const handleRemoveCustomColor = (key: string) => {
		const currentColors = { ...(localBranding.additionalColors || {}) };
		delete currentColors[key];
		updateBrandingField('additionalColors', Object.keys(currentColors).length > 0 ? currentColors : undefined);
	};

	const handleUpdateCustomColorKey = (oldKey: string, newKey: string) => {
		if (oldKey === newKey || !newKey.trim()) return;
		const currentColors = localBranding.additionalColors || {};
		const newColors: Record<string, string> = {};
		for (const [key, value] of Object.entries(currentColors)) {
			if (key === oldKey) {
				newColors[newKey] = value;
			} else {
				newColors[key] = value;
			}
		}
		updateBrandingField('additionalColors', newColors);
	};

	const handleUpdateCustomColorValue = (key: string, value: string) => {
		const currentColors = localBranding.additionalColors || {};
		updateBrandingField('additionalColors', { ...currentColors, [key]: value });
	};

	// Branding AI Assistant handlers
	const handleBrandingAssistantOpenChange = React.useCallback(
		(open: boolean) => {
			setBrandingAssistantOpen(open);
			if (!open) {
				// Show loading state while we wait for fresh data after AI dialog closes
				setWaitingForAIRefresh(true);
				// Refresh branding settings when dialog closes
				invalidateBrandingSettingsQueries({ impersonationContext });
			} else {
				invalidateAllAIConversationFilterQueries();
			}
		},
		[impersonationContext]
	);

	return (
		<div className='settings-admin-company-container'>
			<p className={css(styleSheet.subsectionDescription)}>
				Configure your brand colors, fonts, and images to customize AI-generated content.
			</p>

			<div className={css(baseStyleSheet.mb3)}>
				<Button variant='secondary' size='sm' onClick={() => setBrandingAssistantOpen(true)}>
					Auto-Discover with AI
				</Button>
			</div>

			<AIAssistantDialog
				open={brandingAssistantOpen}
				onOpenChange={handleBrandingAssistantOpenChange}
				assistantName='BrandingAgent'
				conversationType={AIConversationType.Default}
				title='Brand Settings Discovery'
				autoStart
				placeholder='Tell me about your brand...'
				impersonationContext={impersonationContext}
				startNewConversation
				customRenderers={[HexColorRenderer]}
			/>

			{brandingQuery.isPending ? (
				<LoadingSpinner type='small' />
			) : (
				<div className={css(styleSheet.brandingContentWrapper)}>
					{waitingForAIRefresh && (
						<div className={css(styleSheet.brandingLoadingOverlay)}>
							<LoadingSpinner type='small' />
						</div>
					)}
					{/* Brand Colors */}
					<div className={css(styleSheet.brandingSubsection)}>
						<div className={css(styleSheet.brandingSubsectionTitle)}>Brand Colors</div>
						<p className={css(styleSheet.subsectionDescription)}>
							Optional. Set your brand colors for AI-generated images.
						</p>
						<div className={css(styleSheet.brandingColorsRow)}>
							<div className={css(styleSheet.brandingColorItem)}>
								<span className={css(styleSheet.brandingColorLabel)}>Primary Color</span>
								{localBranding.primaryColor ? (
									<div className={css(styleSheet.brandingColorWithClear)}>
										<ColorPicker
											value={localBranding.primaryColor}
											onChange={color => updateBrandingField('primaryColor', color)}
										/>
										<Button variant='link' size='sm' onClick={() => updateBrandingField('primaryColor', undefined)}>
											Clear
										</Button>
									</div>
								) : (
									<Button variant='secondary' size='sm' onClick={() => updateBrandingField('primaryColor', '#000000')}>
										Set Color
									</Button>
								)}
							</div>
							<div className={css(styleSheet.brandingColorItem)}>
								<span className={css(styleSheet.brandingColorLabel)}>Secondary Color</span>
								{localBranding.secondaryColor ? (
									<div className={css(styleSheet.brandingColorWithClear)}>
										<ColorPicker
											value={localBranding.secondaryColor}
											onChange={color => updateBrandingField('secondaryColor', color)}
										/>
										<Button variant='link' size='sm' onClick={() => updateBrandingField('secondaryColor', undefined)}>
											Clear
										</Button>
									</div>
								) : (
									<Button
										variant='secondary'
										size='sm'
										onClick={() => updateBrandingField('secondaryColor', '#666666')}
									>
										Set Color
									</Button>
								)}
							</div>
							<div className={css(styleSheet.brandingColorItem)}>
								<span className={css(styleSheet.brandingColorLabel)}>Accent Color</span>
								{localBranding.accentColor ? (
									<div className={css(styleSheet.brandingColorWithClear)}>
										<ColorPicker
											value={localBranding.accentColor}
											onChange={color => updateBrandingField('accentColor', color)}
										/>
										<Button variant='link' size='sm' onClick={() => updateBrandingField('accentColor', undefined)}>
											Clear
										</Button>
									</div>
								) : (
									<Button variant='secondary' size='sm' onClick={() => updateBrandingField('accentColor', '#0066cc')}>
										Set Color
									</Button>
								)}
							</div>
						</div>

						{/* Custom Named Colors */}
						<div className={css(styleSheet.brandingCustomColorsSection)}>
							<span className={css(styleSheet.brandingColorLabel)}>Custom Colors</span>
							{localBranding.additionalColors && Object.keys(localBranding.additionalColors).length > 0 && (
								<div className={css(styleSheet.brandingCustomColorsList)}>
									{Object.entries(localBranding.additionalColors).map(([key, value]) => (
										<div key={key} className={css(styleSheet.brandingCustomColorItem)}>
											<input
												type='text'
												defaultValue={key}
												onBlur={e => {
													const newKey = e.target.value.trim();
													if (newKey && newKey !== key) {
														handleUpdateCustomColorKey(key, newKey);
													} else if (!newKey) {
														e.target.value = key;
													}
												}}
												className={css(styleSheet.brandingCustomColorName)}
												placeholder='Color name'
											/>
											<ColorPicker value={value} onChange={color => handleUpdateCustomColorValue(key, color)} />
											<Button
												variant='link'
												size='sm'
												onClick={() => handleRemoveCustomColor(key)}
												title='Remove color'
											>
												<TrashIcon />
											</Button>
										</div>
									))}
								</div>
							)}
							<Button variant='secondary' size='sm' onClick={handleAddCustomColor}>
								+ Add Custom Color
							</Button>
						</div>
					</div>

					{/* Brand Font */}
					<div className={css(styleSheet.brandingSubsection)}>
						<div className={css(styleSheet.brandingSubsectionTitle)}>Brand Font</div>
						<div className={css(styleSheet.brandingFontSelect)}>
							<FontPicker
								value={{
									fontName: localBranding.font?.fontName || '',
									fontUri: localBranding.font?.fontUri || '',
								}}
								onChange={font =>
									updateBrandingField('font', {
										fontName: font.fontName,
										fontUri: font.fontUri,
									})
								}
							/>
						</div>
					</div>

					{/* Brand Images */}
					<div className={css(styleSheet.brandingSubsection)}>
						<div className={css(styleSheet.brandingSubsectionTitle)}>Brand Images</div>
						<p className={css(styleSheet.subsectionDescription)}>
							Upload brand images like logos and reference images for AI-generated content.
						</p>
						{localBranding.images && localBranding.images.length > 0 ? (
							<div className={css(styleSheet.brandingImageList)}>
								{localBranding.images.map((image, index) => (
									<div key={image.fileAttachmentId || index} className={css(styleSheet.brandingImageRow)}>
										{editingImageIndex === index ? (
											// Edit mode
											<div className={css(styleSheet.brandingImageEditMode)}>
												{/* Thumbnail preview */}
												{image.fileAttachment?.url && (
													<div className={css(styleSheet.brandingImageEditThumbnail)}>
														<img
															src={image.fileAttachment.url}
															alt={image.name || 'Brand image'}
															className={css(styleSheet.brandingImageEditThumbnailImg)}
														/>
													</div>
												)}
												<div className={css(styleSheet.brandingImageEditFields)}>
													<div className={css(styleSheet.brandingImageFieldGroup)}>
														<label className={css(styleSheet.brandingImageFieldLabel)}>Name</label>
														<input
															type='text'
															placeholder='e.g., Logo, Brand Reference'
															value={image.name || ''}
															onChange={e => handleUpdateImageField(index, 'name', e.target.value)}
															className={css(baseStyleSheet.textFieldStandard)}
														/>
													</div>
													<div className={css(styleSheet.brandingImageFieldGroup)}>
														<label className={css(styleSheet.brandingImageFieldLabel)}>
															Description <span className={css(styleSheet.requiredIndicator)}>*</span>
														</label>
														<input
															type='text'
															placeholder='How should AI use this image? e.g., "Primary logo for headers"'
															value={image.description || ''}
															onChange={e => handleUpdateImageField(index, 'description', e.target.value)}
															className={css(
																baseStyleSheet.textFieldStandard,
																!image.description && styleSheet.brandingImageDescriptionRequired
															)}
														/>
														<span className={css(styleSheet.brandingImageFieldHint)}>
															Help AI understand when and how to use this image
														</span>
													</div>
												</div>
												<div className={css(styleSheet.brandingImageEditActions)}>
													<Button
														variant='primary'
														size='sm'
														onClick={() => setEditingImageIndex(null)}
														disabled={!image.description?.trim()}
														title={!image.description?.trim() ? 'Please add a description' : undefined}
													>
														Done
													</Button>
													<Button
														variant='link'
														size='sm'
														onClick={() => handleRemoveImage(index)}
														title='Remove image'
													>
														<TrashIcon />
													</Button>
												</div>
											</div>
										) : (
											// View mode
											<div className={css(styleSheet.brandingImageViewMode)}>
												<div className={css(styleSheet.brandingImageViewContent)}>
													<div className={css(styleSheet.brandingImageFieldGroup)}>
														<span className={css(styleSheet.brandingImageFieldLabel)}>Name</span>
														<button
															type='button'
															className={css(styleSheet.brandingImageNameLink)}
															onClick={() => setPreviewImage(image)}
															title='Click to view image'
														>
															{image.name || 'Untitled'}
														</button>
													</div>
													{image.description && (
														<div className={css(styleSheet.brandingImageFieldGroup)}>
															<span className={css(styleSheet.brandingImageFieldLabel)}>Description</span>
															<span className={css(styleSheet.brandingImageFieldValue)}>{image.description}</span>
														</div>
													)}
												</div>
												<Button variant='link' size='sm' onClick={() => setEditingImageIndex(index)} title='Edit image'>
													<EditItemPenIcon />
												</Button>
											</div>
										)}
									</div>
								))}
							</div>
						) : (
							<p className={css(styleSheet.brandingEmptyState)}>No brand images configured.</p>
						)}
						<input
							ref={fileInputRef}
							type='file'
							accept='image/*'
							style={{ display: 'none' }}
							onChange={handleFileSelected}
						/>
						<Button
							variant='secondary'
							size='sm'
							onClick={handleAddImageClick}
							disabled={uploadingImage}
							styles={[styleSheet.brandingAddImageButton]}
						>
							{uploadingImage ? (
								<>
									<LoadingSpinner type='small' /> Uploading...
								</>
							) : (
								'+ Add Image'
							)}
						</Button>
					</div>

					{/* Image Preview Modal */}
					<ReactModal
						isOpen={!!previewImage}
						onRequestClose={() => setPreviewImage(null)}
						style={imagePreviewModalStyles}
					>
						{previewImage && (
							<div className={css(styleSheet.imagePreviewModal)}>
								<div className={css(styleSheet.imagePreviewHeader)}>
									<div>
										<div className={css(styleSheet.imagePreviewTitle)}>{previewImage.name || 'Untitled'}</div>
										{previewImage.fileAttachment?.fileName && (
											<div className={css(styleSheet.imagePreviewFilename)}>{previewImage.fileAttachment.fileName}</div>
										)}
									</div>
									<DeprecatedCloseButton onClick={() => setPreviewImage(null)} />
								</div>
								{previewImage.fileAttachment?.url ? (
									<img
										src={previewImage.fileAttachment.url}
										alt={previewImage.name || 'Brand image'}
										className={css(styleSheet.imagePreviewImg)}
									/>
								) : (
									<div className={css(styleSheet.imagePreviewNoImage)}>No image available</div>
								)}
							</div>
						)}
					</ReactModal>

					{/* Brand Description */}
					<div className={css(styleSheet.brandingSubsection)}>
						<div className={css(styleSheet.brandingSubsectionTitle)}>Brand Description</div>
						<p className={css(styleSheet.subsectionDescription)}>
							Describe your brand style and tone for AI-generated content.
						</p>
						<textarea
							className={css(styleSheet.brandingTextarea)}
							placeholder='e.g., Professional and modern with a friendly tone. Use clean, minimalist designs with bold typography.'
							value={localBranding.brandDescription || ''}
							onChange={e => updateBrandingField('brandDescription', e.target.value)}
						/>
					</div>

					{/* Branding Save/Cancel/Clear */}
					<div className={css(styleSheet.brandingButtonRow)}>
						<div className={css(styleSheet.buttonContainer)}>
							<Button
								variant='primary'
								size='sm'
								onClick={handleSaveBranding}
								disabled={!brandingDirty || brandingMutation.isPending}
							>
								{brandingMutation.isPending ? 'Saving...' : 'Save'}
							</Button>
							<Button variant='secondary' size='sm' onClick={handleCancelBranding} disabled={!brandingDirty}>
								Cancel
							</Button>
							<Button variant='secondary' size='sm' onClick={handleClearAllBranding}>
								Clear
							</Button>
						</div>
					</div>
				</div>
			)}
		</div>
	);
};

export const BrandSettingsSection = React.memo(BrandSettingsSectionComponent);
